import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '../store/authStore';

// In development, prefer the API gateway directly so browser requests don't depend on the
// Vite proxy. Use the loopback IP explicitly because localhost can resolve differently in this setup.
export const API_BASE_URL = import.meta.env.VITE_API_URL ?? (import.meta.env.DEV ? 'http://127.0.0.1:3001' : '');

// Create axios instance with security headers
export const apiClient = axios.create({
  baseURL: `${API_BASE_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
    'X-Content-Type-Options': 'nosniff',
    'X-Frame-Options': 'DENY',
    'X-XSS-Protection': '1; mode=block',
  },
  timeout: 30000,
  withCredentials: true, // Enable CSRF tokens
});

// CSRF Token storage
let csrfToken: string | null = null;

// Silent-refresh coordination. A single in-flight refresh is shared by every
// request that 401s at the same time, so a burst of parallel calls triggers one
// refresh, not one per request.
let refreshInFlight: Promise<string | null> | null = null;

/**
 * Try to renew the access token using the existing /auth/refresh endpoint.
 * Uses a bare axios call (not apiClient) to avoid recursing through this
 * interceptor. Returns the new token, or null if the session can't be renewed
 * (token genuinely expired/invalid) so the caller can fall back to logout.
 */
async function tryRefreshToken(): Promise<string | null> {
  const currentToken = useAuthStore.getState().token;
  if (!currentToken) return null;

  if (!refreshInFlight) {
    refreshInFlight = axios
      .post(
        `${API_BASE_URL}/api/auth/refresh`,
        { token: currentToken },
        { withCredentials: true, headers: { 'Content-Type': 'application/json' } }
      )
      .then((res) => {
        const newToken = res.data?.token ?? null;
        if (newToken) {
          useAuthStore.getState().setToken(newToken);
        }
        return newToken;
      })
      .catch(() => null)
      .finally(() => {
        refreshInFlight = null;
      });
  }

  return refreshInFlight;
}

// Request interceptor - Add auth token and CSRF token
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = useAuthStore.getState().token;

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Add CSRF token if available
    if (csrfToken) {
      config.headers['X-CSRF-Token'] = csrfToken;
    }

    // Add request ID for tracking
    config.headers['X-Request-ID'] = crypto.randomUUID();

    return config;
  },
  (error) => {
    console.error('Request error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor - Handle errors and CSRF tokens
apiClient.interceptors.response.use(
  (response) => {
    // Extract CSRF token from response headers if present
    const newCsrfToken = response.headers['x-csrf-token'];
    if (newCsrfToken) {
      csrfToken = newCsrfToken;
    }

    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Handle 401 Unauthorized
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const requestUrl = originalRequest?.url || '';
      // Never try to refresh/redirect for the auth endpoints themselves — a 401
      // there is a real credential failure the calling code already handles.
      const isAuthRequest =
        requestUrl.includes('/auth/login') ||
        requestUrl.includes('/auth/register') ||
        requestUrl.includes('/auth/sso-login') ||
        requestUrl.includes('/auth/refresh');
      const isAuthPage =
        window.location.pathname === '/login' ||
        window.location.pathname === '/register' ||
        window.location.pathname === '/auth/callback' ||
        window.location.pathname === '/callback' ||
        window.location.pathname === '/forgot-password';

      if (isAuthRequest) {
        return Promise.reject(error);
      }

      // Attempt a silent refresh using the existing /auth/refresh endpoint before
      // giving up. If the token is still valid this renews it and the original
      // request is retried transparently — no redirect, no login-page flash.
      const newToken = await tryRefreshToken();
      if (newToken) {
        originalRequest.headers = originalRequest.headers ?? {};
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return apiClient(originalRequest);
      }

      // Refresh failed: the session is genuinely expired/invalid. Clear it and
      // send the user to login (unless we are already on an auth page).
      if (!isAuthPage) {
        const { logout } = useAuthStore.getState();
        logout();

        sessionStorage.setItem('redirectAfterLogin', window.location.pathname);
        window.location.href = '/login';
      }

      return Promise.reject(error);
    }

    // Handle 403 Forbidden (CSRF token invalid)
    if (error.response?.status === 403) {
      // Refresh CSRF token and retry
      try {
        const csrfResponse = await axios.get(`${API_BASE_URL}/api/csrf-token`, {
          withCredentials: true,
        });

        csrfToken = csrfResponse.data.csrfToken;

        // Retry original request with new CSRF token
        if (originalRequest) {
          originalRequest.headers['X-CSRF-Token'] = csrfToken;
          return apiClient(originalRequest);
        }
      } catch (csrfError) {
        console.error('Failed to refresh CSRF token:', csrfError);
      }
    }

    // Handle network errors
    if (!error.response) {
      console.error('Network error - please check your connection');
    }

    return Promise.reject(error);
  }
);

// Fetch initial CSRF token
export async function initializeCSRF() {
  try {
    const response = await axios.get(`${API_BASE_URL}/api/csrf-token`, {
      withCredentials: true,
    });

    csrfToken = response.data.csrfToken;
  } catch (error) {
    console.error('Failed to fetch CSRF token:', error);
  }
}

export default apiClient;
